import { createAction, props } from "@ngrx/store";
import {
  AutomationApplication,
  AutomationPage,
  AutomationUiObject,
  CommandDfinition,
  ReusableComponent,
  ReusableScript,
  ScriptGroup,
  Service,
  TestScript,
} from "../../models/automation-fields";

/** COMMON FAILURE ACTION **/
const automationFailure = createAction(
  "[Automation] Failure",
  props<{ error: string }>()
);

/** LOAD COMMAND DEFINITIONS */
const loadCommandDefinitions = createAction(
  "[Automation] Load Command Definitions"
);
const loadCommandDefinitionsSuccess = createAction(
  "[Automation] Load Command Definitions Success",
  props<{ commandDefinitions: CommandDfinition[] }>()
);

/** APPLICATION ACTIONS **/
const loadApplications = createAction(
  "[Automation] Load Applications",
  props<{ projectId: string; automationProjectId: string }>()
);
const loadApplicationsSuccess = createAction(
  "[Automation] Load Applications Success",
  props<{
    automationProjectId: string;
    applications: AutomationApplication[];
  }>()
);
const addApplication = createAction(
  "[Automation] Add Application",
  props<{
    projectId: string;
    automationProjectId: string;
    application: any;
  }>()
);
const addApplicationSuccess = createAction(
  "[Automation] Add Application Success",
  props<{ automationProjectId: string; application: AutomationApplication }>()
);
const deleteApplication = createAction(
  "[Automation] Delete Application",
  props<{ projectId: string; automationProjectId: string; id: string }>()
);
const deleteApplicationSuccess = createAction(
  "[Automation] Delete Application Success",
  props<{ automationProjectId: string; id: string }>()
);

const deleteApplications = createAction(
  "[Automation] Delete Applications",
  props<{ projectId: string; automationProjectId: string; idList: [] }>()
);

const deleteApplicationsSuccess = createAction(
  "[Automation] Delete Applications Success",
  props<{ automationProjectId: string; idList: string[] }>()
);

const updateApplication = createAction(
  "[Automation] Update Application",
  props<{
    projectId: string;
    automationProjectId: string;
    id: string;
    changes: any;
  }>()
);
const updateApplicationSuccess = createAction(
  "[Automation] Update Application Success",
  props<{ automationProjectId: string; application: AutomationApplication }>()
);

/** PAGE ACTIONS **/
const loadPages = createAction(
  "[Automation] Load Pages",
  props<{
    projectId: string;
    automationProjectId: string;
    applicationId: string;
  }>()
);
const loadPagesSuccess = createAction(
  "[Automation] Load Pages Success",
  props<{ applicationId: string; pages: AutomationPage[] }>()
);
const addPage = createAction(
  "[Automation] Add Page",
  props<{
    projectId: string;
    automationProjectId: string;
    applicationId: string;
    page: any;
  }>()
);
const addPageSuccess = createAction(
  "[Automation] Add Page Success",
  props<{ applicationId: string; page: AutomationPage }>()
);
const deletePage = createAction(
  "[Automation] Delete Page",
  props<{
    projectId: string;
    automationProjectId: string;
    applicationId: string;
    id: string;
  }>()
);
const deletePageSuccess = createAction(
  "[Automation] Delete Page Success",
  props<{ applicationId: string; id: string }>()
);

const deletePages = createAction(
  "[Automation] Delete Pages",
  props<{
    projectId: string;
    automationProjectId: string;
    applicationId: string;
    idList: [];
  }>()
);
const deletePagesSuccess = createAction(
  "[Automation] Delete Pages Success",
  props<{ applicationId: string; idList: string[] }>()
);
const updatePage = createAction(
  "[Automation] Update Page",
  props<{
    projectId: string;
    automationProjectId: string;
    applicationId: string;
    id: string;
    changes: Partial<AutomationPage>;
  }>()
);
const updatePageSuccess = createAction(
  "[Automation] Update Page Success",
  props<{ applicationId: string; page: AutomationPage }>()
);

/** UI OBJECT ACTIONS **/
const loadUiObjects = createAction(
  "[Automation] Load UI Objects",
  props<{
    projectId: string;
    automationProjectId: string;
    applicationId: string;
    pageId: string;
  }>()
);

const loadUiObjectsSuccess = createAction(
  "[Automation] Load UI Objects Success",
  props<{ pageId: string; uiObjects: any[] }>()
);

const addUiObject = createAction(
  "[Automation] Add UI Object",
  props<{
    projectId: string;
    automationProjectId: string;
    applicationId: string;
    pageId: string;
    uiObject: any;
  }>()
);

const addUiObjectSuccess = createAction(
  "[Automation] Add UI Object Success",
  props<{ pageId: string; uiObject: any }>()
);

const deleteUiObject = createAction(
  "[Automation] Delete UI Object",
  props<{
    projectId: string;
    automationProjectId: string;
    applicationId: string;
    pageId: string;
    id: string;
  }>()
);

const deleteUiObjectsSuccess = createAction(
  "[Automation] Delete UI Objects Success",
  props<{ pageId: string; idList: string[] }>()
);

const deleteUiObjects = createAction(
  "[Automation] Delete UI Objects",
  props<{
    projectId: string;
    automationProjectId: string;
    applicationId: string;
    pageId: string;
    idList: [];
  }>()
);

const deleteUiObjectSuccess = createAction(
  "[Automation] Delete UI Object Success",
  props<{ pageId: string; id: string }>()
);

const updateUiObject = createAction(
  "[Automation] Update UI Object",
  props<{
    projectId: string;
    automationProjectId: string;
    applicationId: string;
    pageId: string;
    id: string;
    changes: Partial<AutomationUiObject>;
    action: any;
  }>()
);

const updateUiObjectSuccess = createAction(
  "[Automation] Update UI Object Success",
  props<{
    applicationId: string;
    pageId: string;
    uiObject: AutomationUiObject;
  }>()
);

/** STANDARD TEST SUITE **/
const loadScriptGroup = createAction(
  "[Automation] Load ScriptGroup",
  props<{ projectId: string; automationProjectId: string }>()
);

const loadScriptGroupSuccess = createAction(
  "[Automation] Load ScriptGroup Success",
  props<{
    scriptGroup: ScriptGroup[];
    testScript: TestScript[];
  }>()
);

const addScriptGroup = createAction(
  "[Automation] Add Test Suite",
  props<{
    projectId: string;
    automationProjectId: string;
    scriptGroup: ScriptGroup;
  }>()
);

const addScriptGroupSuccess = createAction(
  "[Automation] Add Test Suite Success",
  props<{ scriptGroup: ScriptGroup }>()
);

const deleteScriptGroup = createAction(
  "[Automation] Delete Test Suite",
  props<{ projectId: string; automationProjectId: string; id: string }>()
);

const deleteScriptGroupSuccess = createAction(
  "[Automation] Delete Test Suite Success",
  props<{ id: string }>()
);

const deleteScriptGroups = createAction(
  "[Automation] Delete Test Suites",
  props<{ projectId: string; automationProjectId: string; idList: any[] }>()
);

const deleteScriptGroupsSuccess = createAction(
  "[Automation] Delete Test Suites Success",
  props<{ idList: any[] }>()
);

const updateScriptGroup = createAction(
  "[Automation] Update ScriptGroup",
  props<{
    projectId: string;
    automationProjectId: string;
    id: string;
    changes: Partial<ScriptGroup>;
  }>()
);

const updateScriptGroupSuccess = createAction(
  "[Automation] Update ScriptGroup Success",
  props<{ scriptGroup: ScriptGroup }>()
);

/** TEST SCRIPT **/
const loadTestScript = createAction(
  "[Automation] Load TestScript",
  props<{ projectId: string; automationProjectId: string }>()
);

const loadTestScriptSuccess = createAction(
  "[Automation] Load TestScript Success",
  props<{
    testScript: TestScript[];
  }>()
);

const addTestScript = createAction(
  "[Automation] Add Test Script",
  props<{
    projectId: string;
    automationProjectId: string;
    testScript: TestScript;
  }>()
);

const addTestScriptSuccess = createAction(
  "[Automation] Add Test Script Success",
  props<{ testScript: TestScript }>()
);

const deleteTestScript = createAction(
  "[Automation] Delete Test Script",
  props<{ projectId: string; automationProjectId: string; id: string }>()
);

const deleteTestScriptSuccess = createAction(
  "[Automation] Delete Test Script Success",
  props<{ id: string }>()
);

const deleteTestScripts = createAction(
  "[Automation] Delete Test Scripts",
  props<{ projectId: string; automationProjectId: string; idList: any[] }>()
);

const deleteTestScriptsSuccess = createAction(
  "[Automation] Delete Test Scripst Success",
  props<{ idList: any[] }>()
);

const updateTestScript = createAction(
  "[Automation] Update TestScript",
  props<{
    projectId: string;
    automationProjectId: string;
    id: string;
    changes: Partial<TestScript>;
  }>()
);

const renameTestScript = createAction(
  "[Automation] Rename TestScript",
  props<{
    projectId: string;
    automationProjectId: string;
    id: string;
    changes: Partial<TestScript>;
  }>()
);

const updateTestScriptSuccess = createAction(
  "[Automation] Update TestScript Success",
  props<{ testScript: TestScript }>()
);

//mapping
const mapToTestScript = createAction(
  "[Automation] Mapping TestScript",
  props<{
    projectId: string;
    automationProjectId: string;
    id: string;
    mappingId: string;
  }>()
);

/** REUSABLE COMPONENT **/
const loadReusableComponent = createAction(
  "[Automation] Load Reusable Component",
  props<{ projectId: string; automationProjectId: string }>()
);

const loadReusableComponentSuccess = createAction(
  "[Automation] Load Reusable Component Success",
  props<{
    reusableComponent: ReusableComponent[];
    reusableScript: ReusableScript[];
  }>()
);

const addReusableComponent = createAction(
  "[Automation] Add Reusable Component",
  props<{
    projectId: string;
    automationProjectId: string;
    reusableComponent: ReusableComponent;
  }>()
);

const addReusableComponentSuccess = createAction(
  "[Automation] Add Reusable Component Success",
  props<{ reusableComponent: ReusableComponent }>()
);

const deleteReusableComponent = createAction(
  "[Automation] Delete Reusable Component",
  props<{ projectId: string; automationProjectId: string; id: string }>()
);

const deleteReusableComponentSuccess = createAction(
  "[Automation] Delete Reusable Component Success",
  props<{ id: string }>()
);

const deleteReusableComponents = createAction(
  "[Automation] Delete Reusable Components",
  props<{ projectId: string; automationProjectId: string; idList: any[] }>()
);

const deleteReusableComponentsSuccess = createAction(
  "[Automation] Delete Reusable Components Success",
  props<{ idList: any[] }>()
);


const updateReusableComponent = createAction(
  "[Automation] Update Reusable Component",
  props<{
    projectId: string;
    automationProjectId: string;
    id: string;
    changes: Partial<ReusableComponent>;
  }>()
);

const updateReusableComponentSuccess = createAction(
  "[Automation] Update Reusable Component Success",
  props<{ reusableComponent: ReusableComponent }>()
);

/** REUSABLE SCRIPT **/
const loadReusableScript = createAction(
  "[Automation] Load Reusable Script",
  props<{ projectId: string; automationProjectId: string }>()
);

const loadReusableScriptSuccess = createAction(
  "[Automation] Load Reusable Script Success",
  props<{
    reusableScript: ReusableScript[];
  }>()
);

const addReusableScript = createAction(
  "[Automation] Add Reusable Script",
  props<{
    projectId: string;
    automationProjectId: string;
    reusableScript: ReusableScript;
  }>()
);

const addReusableScriptSuccess = createAction(
  "[Automation] Add Reusable Script Success",
  props<{ reusableScript: ReusableScript }>()
);

const deleteReusableScript = createAction(
  "[Automation] Delete Reusable Script",
  props<{ projectId: string; automationProjectId: string; id: string }>()
);

const deleteReusableScriptSuccess = createAction(
  "[Automation] Delete Reusable Script Success",
  props<{ id: string }>()
);


const deleteReusableScripts = createAction(
  "[Automation] Delete Reusable Scripts",
  props<{ projectId: string; automationProjectId: string; idList: any[] }>()
);

const deleteReusableScriptsSuccess = createAction(
  "[Automation] Delete Reusable Scripts Success",
  props<{ idList: any[] }>()
);


const updateReusableScript = createAction(
  "[Automation] Update Reusable Script",
  props<{
    projectId: string;
    automationProjectId: string;
    id: string;
    changes: Partial<ReusableScript>;
  }>()
);

const updateReusableScriptSuccess = createAction(
  "[Automation] Update Reusable Script Success",
  props<{ reusableScript: ReusableScript }>()
);

// services
const loadService = createAction(
  "[Automation] Load Service",
  props<{ projectId: string; automationProjectId: string }>()
);

const loadServiceSuccess = createAction(
  "[Automation] Load Service Success",
  props<{
    apiServices: Service[];
  }>()
);

const addService = createAction(
  "[Automation] Add Service",
  props<{
    projectId: string;
    automationProjectId: string;
    apiService: any;
  }>()
);

const addServiceSuccess = createAction(
  "[Automation] Add Service Success",
  props<{ service: any }>()
);


const deleteServices = createAction(
  "[Automation] Delete Services",
  props<{ projectId: string; automationProjectId: string; idList: any[] }>()
);

const deleteServicesSuccess = createAction(
  "[Automation] Delete Services Success",
  props<{ idList: any[] }>()
);

const updateService = createAction(
  "[Automation] Update Service",
  props<{
    projectId: string;
    automationProjectId: string;
    id: string;
    changes: Partial<any>;
  }>()
);

const updateServiceSuccess = createAction(
  "[Automation] Update Service Success",
  props<{ service: any }>()
);

//service operations
const loadServiceOperation = createAction(
  "[Automation] Load Service Operation",
  props<{ projectId: string; automationProjectId: string, serviceId: string }>()
);

const loadServiceOperationSuccess = createAction(
  "[Automation] Load Service Operation Success",
  props<{
    serviceOperations: Service[];
  }>()
);

const addServiceOperation = createAction(
  "[Automation] Add Service Operation",
  props<{
    projectId: string;
    automationProjectId: string;
    serviceId: string;
    serviceOperations: any;
  }>()
);

const addServiceOperationSuccess = createAction(
  "[Automation] Add Service Operation Success",
  props<{ serviceOperations: any }>()
);

const deleteServiceOperations = createAction(
  "[Automation] Delete Service Operations",
  props<{ projectId: string; automationProjectId: string; serviceId: string ,idList: any[] }>()
);

const deleteServiceOperationsSuccess = createAction(
  "[Automation] Delete Service Operations Success",
  props<{ idList: any[] }>()
);

const updateServiceOperation = createAction(
  "[Automation] Update Service Operation",
  props<{
    projectId: string;
    automationProjectId: string;
    serviceId: string ,
    id: string;
    changes: Partial<any>;
  }>()
);

const updateServiceOperationSuccess = createAction(
  "[Automation] Update Service Operation Success",
  props<{ serviceOperations: any }>()
);

//operation parameters
const loadOperationParameters = createAction(
  "[Automation] Load Operation Parameters",
  props<{ projectId: string; automationProjectId: string, serviceId: string, operationId:string }>()
);

const loadOperationParametersSuccess = createAction(
  "[Automation] Load Operation Parameters Success",
  props<{
    operationParameters: Service[];
  }>()
);

const addOperationParameter = createAction(
  "[Automation] Add Operation Parameters",
  props<{
    projectId: string;
    automationProjectId: string;
    serviceId: string;
    operationId:string ;
    operationParameters: any;
  }>()
);

const addOperationParameterSuccess = createAction(
  "[Automation] Add Operation Parameters Success",
  props<{ operationParameters: any }>()
);

const deleteOperationParameters = createAction(
  "[Automation] Delete Operation Parameters",
  props<{ projectId: string; automationProjectId: string; serviceId: string , operationId:string ,idList: any[] }>()
);

const deleteOperationParametersSuccess = createAction(
  "[Automation] Delete Operation Parameters Success",
  props<{ idList: any[] }>()
);

const updateOperationParameter = createAction(
  "[Automation] Update Operation Parameters",
  props<{
    projectId: string;
    automationProjectId: string;
    serviceId: string ,
    operationId:string ,
    id: string;
    changes: Partial<any>;
  }>()
);

const updateOperationParametersSuccess = createAction(
  "[Automation] Update Operation Parameters Success",
  props<{ operationParameters: any }>()
);


//operation headers
const loadOperationHeader = createAction(
  "[Automation] Load Operation Header",
  props<{ projectId: string; automationProjectId: string, serviceId: string , operationId:string}>()
);

const loadOperationHeaderSuccess = createAction(
  "[Automation] Load Operation Header Success",
  props<{
    operationHeaders: Service[];
  }>()
);

const addOperationHeader = createAction(
  "[Automation] Add Operation Header",
  props<{
    projectId: string;
    automationProjectId: string;
    serviceId: string;
    operationId: string;
    operationHeaders: any;
  }>()
);

const addOperationHeaderSuccess = createAction(
  "[Automation] Add Operation Header Success",
  props<{ operationHeaders: any }>()
);

const deleteOperationHeader = createAction(
  "[Automation] Delete Operation Header",
  props<{ projectId: string; automationProjectId: string; serviceId: string , operationId:string ,idList: any[] }>()
);

const deleteOperationHeaderSuccess = createAction(
  "[Automation] Delete OperationHeader Success",
  props<{ idList: any[] }>()
);

const updateOperationHeader = createAction(
  "[Automation] Update Operation Header",
  props<{
    projectId: string;
    automationProjectId: string;
    serviceId: string ,
    operationId:string ,
    id: string;
    changes: Partial<any>;
  }>()
);

const updateOperationHeaderSuccess = createAction(
  "[Automation] Update Operation Header Success",
  props<{ operationHeaders: any }>()
);

/** CLEAR AUTOMATION STATE **/
const clearAutomationState = createAction('[Automation] Clear State');

export const AutomationActions = {
  clearAutomationState,
  automationFailure,
  loadApplications,
  loadApplicationsSuccess,
  addApplication,
  addApplicationSuccess,
  deleteApplication,
  deleteApplicationSuccess,
  deleteApplications,
  deleteApplicationsSuccess,
  updateApplication,
  updateApplicationSuccess,
  loadPages,
  loadPagesSuccess,
  addPageSuccess,
  deletePageSuccess,
  deletePage,
  deletePagesSuccess,
  deletePages,
  addPage,
  updatePageSuccess,
  updatePage,
  updateUiObjectSuccess,
  updateUiObject,
  deleteUiObjectSuccess,
  deleteUiObject,
  deleteUiObjectsSuccess,
  deleteUiObjects,
  addUiObjectSuccess,
  addUiObject,
  loadUiObjectsSuccess,
  loadUiObjects,
  loadCommandDefinitions,
  loadCommandDefinitionsSuccess,
  loadScriptGroup,
  loadScriptGroupSuccess,
  addScriptGroup,
  addScriptGroupSuccess,
  deleteScriptGroup,
  deleteScriptGroupSuccess,
  deleteScriptGroups,
  deleteScriptGroupsSuccess,
  updateScriptGroup,
  updateScriptGroupSuccess,
  loadTestScript,
  loadTestScriptSuccess,
  addTestScript,
  addTestScriptSuccess,
  deleteTestScript,
  deleteTestScriptSuccess,
  deleteTestScripts,
  deleteTestScriptsSuccess,
  updateTestScript,
  renameTestScript,
  updateTestScriptSuccess,
  loadReusableComponent,
  loadReusableComponentSuccess,
  addReusableComponent,
  addReusableComponentSuccess,
  deleteReusableComponent,
  deleteReusableComponentSuccess,
  deleteReusableComponents,
  deleteReusableComponentsSuccess,
  updateReusableComponent,
  updateReusableComponentSuccess,
  loadReusableScript,
  loadReusableScriptSuccess,
  addReusableScript,
  addReusableScriptSuccess,
  deleteReusableScript,
  deleteReusableScriptSuccess,
  updateReusableScript,
  deleteReusableScripts,
  deleteReusableScriptsSuccess,
  updateReusableScriptSuccess,
  mapToTestScript,
  loadService,
  loadServiceSuccess,
  addService,
  addServiceSuccess,
  updateService,
  updateServiceSuccess,
  deleteServices,
  deleteServicesSuccess,
  loadServiceOperation,
  loadServiceOperationSuccess,
  addServiceOperation,
  addServiceOperationSuccess,
  updateServiceOperation,
  updateServiceOperationSuccess,
  deleteServiceOperations,
  deleteServiceOperationsSuccess,
  loadOperationParameters,
  loadOperationParametersSuccess,
  addOperationParameter,
  addOperationParameterSuccess,
  updateOperationParameter,
  updateOperationParametersSuccess,
  deleteOperationParameters,
  deleteOperationParametersSuccess,
  loadOperationHeader,
  loadOperationHeaderSuccess,
  addOperationHeader,
  addOperationHeaderSuccess,
  updateOperationHeader,
  updateOperationHeaderSuccess,
  deleteOperationHeader,
  deleteOperationHeaderSuccess
};
